import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { dashboardAPI } from '../../utils/api';
import { formatCurrency, formatDate, daysUntilExpiry } from '../../utils/helpers';
import StatCard from '../../components/StatCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

function Dashboard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, trendRes] = await Promise.all([
          dashboardAPI.get(),
          dashboardAPI.getTrend()
        ]);
        setData(dashRes.data);
        setTrend(trendRes.data);
        if (dashRes.data.lowStockAlerts?.length > 0) {
          toast(`⚠️ ${dashRes.data.lowStockAlerts.length} supplement(s) low on stock!`, { duration: 4000 });
        }
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingSpinner message="Loading dashboard..." />;
  if (!data) return null;

  const pieData = [
    { name: 'Membership', value: data.income?.membership || 0 },
    { name: 'Supplements', value: data.income?.supplements || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
          <p className="text-gray-500 text-sm mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate('/members/add')} className="btn-primary text-sm">
            + Add Member
          </button>
          <button onClick={() => navigate('/attendance')} className="btn-secondary text-sm">
            Mark Attendance
          </button>
          <button onClick={() => navigate('/payments')} className="btn-secondary text-sm">
            Record Payment
          </button>
          <button onClick={() => navigate('/expenses')} className="btn-secondary text-sm">
            Add Expense
          </button>
        </div>
      </div>

      {/* Attendance Cards */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Today's Attendance</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Morning Shift"
            value={data.attendance?.morning || 0}
            subtitle="members checked in"
            icon="🌅"
            color="yellow"
          />
          <StatCard
            title="Evening Shift"
            value={data.attendance?.evening || 0}
            subtitle="members checked in"
            icon="🌆"
            color="blue"
          />
          <StatCard
            title="Today's Collection"
            value={formatCurrency(data.todayCollection)}
            subtitle="payments received"
            icon="💰"
            color="green"
          />
          <StatCard
            title="Pending Dues"
            value={data.pendingCount || 0}
            subtitle="members with dues"
            icon="⚠️"
            color="red"
          />
        </div>
      </div>

      {/* Monthly Summary */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">This Month</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            title="Total Income"
            value={formatCurrency(data.income?.total)}
            subtitle={`Membership: ${formatCurrency(data.income?.membership)} | Supplements: ${formatCurrency(data.income?.supplements)}`}
            icon="📈"
            color="green"
          />
          <StatCard
            title="Total Expenses"
            value={formatCurrency(data.expenses)}
            subtitle="all categories"
            icon="📉"
            color="red"
          />
          <StatCard
            title="Net Profit"
            value={formatCurrency(data.netProfit)}
            subtitle={data.netProfit >= 0 ? 'Profitable month' : 'Running at loss'}
            icon="💹"
            color={data.netProfit >= 0 ? 'green' : 'red'}
          />
        </div>
      </div>

      {/* Member Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Total Members" value={data.memberStats?.total || 0} icon="👥" color="blue" />
        <StatCard title="Active Members" value={data.memberStats?.active || 0} icon="✅" color="green" />
        <StatCard title="Expired" value={data.memberStats?.expired || 0} icon="❌" color="red" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="card">
          <h3 className="text-base font-semibold text-gray-800 mb-4">6-Month Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="income" name="Income" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Split */}
        <div className="card">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Revenue Split (This Month)</h3>
          {(data.income?.total || 0) > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No revenue data this month
            </div>
          )}
        </div>
      </div>

      {/* Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expiring Members */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-800">Expiring Soon (7 days)</h3>
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
              {data.expiringMembers?.length || 0} members
            </span>
          </div>
          {data.expiringMembers?.length > 0 ? (
            <div className="space-y-2">
              {data.expiringMembers.map(m => (
                <div key={m._id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.name}</p>
                    <p className="text-xs text-gray-500">{m.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-yellow-700">{formatDate(m.expiryDate)}</p>
                    <p className="text-xs text-gray-500">{daysUntilExpiry(m.expiryDate)} days left</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-4">No members expiring in 7 days</p>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-800">Low Stock Alerts</h3>
            {data.lowStockAlerts?.length > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                {data.lowStockAlerts.length} item(s)
              </span>
            )}
          </div>
          {data.lowStockAlerts?.length > 0 ? (
            <div className="space-y-2">
              {data.lowStockAlerts.map(s => (
                <div key={s._id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.brand}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-red-600">{s.stock} units left</p>
                    <p className="text-xs text-gray-400">Min: {s.lowStockThreshold}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-4">All supplements are well stocked</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
